'use client'

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { createContext, ReactNode, useContext } from 'react'
import { FilterAssembly, InputAssembly, InputAttachedFlyout } from '../../structural'
import { DynamicControlProps, useDynamicControl } from '../../utilities'

type Option<T> = {
	value: T
	label: ReactNode
}

export type SorterProps<T, O extends Option<T>> =
	& { items: Array<O>; label: string }
	& DynamicControlProps<T>

export function Sorter<T, O extends Option<T>>(
	{ label, items, defaultValue, value, onChange }: SorterProps<T, O>,
) {
	const { value: innerValue, onChange: innerOnChange } = useDynamicControl({ defaultValue, value, onChange })
	const { showIcon, showLabel } = useContext(SorterConfigContext)

	return (
		<div className={`${showIcon && 'flex'} gap-2 items-center justify-center`}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width={14}
				height={14}
				viewBox="0 0 2500 2500"
				className={`mr-1 text-grey-600 relative top-0.5 hidden ${showIcon && 'md:block'}`}
			>
				<path
					d="M 923.627 84.554 C 892.558 90.727, 864.666 116.624, 855.828 147.500 C 853.863 154.367, 853.536 157.838, 853.519 172 C 853.503 186.203, 853.815 189.585, 855.760 196.298 C 861.871 217.382, 875.373 235.450, 894.206 247.744 C 903.420 253.760, 911.951 257.285, 923.117 259.693 C 931.072 261.408, 943.841 261.500, 1173 261.500 C 1402.159 261.500, 1414.928 261.408, 1422.883 259.693 C 1434.058 257.283, 1442.581 253.759, 1451.825 247.724 C 1472.623 234.147, 1486.812 213.560, 1491.549 190.090 C 1493.863 178.621, 1493.127 157.699, 1490.032 147 C 1480.978 115.701, 1451.400 89.165, 1419.861 84.047 C 1415.603 83.356, 1333.640 83.044, 1172 83.102 C 972.171 83.173, 929.314 83.424, 923.627 84.554 M 1897.393 84.023 C 1877.125 87.672, 1859.640 97.894, 1844.878 114.723 C 1834.901 126.097, 1827.473 141.941, 1825.058 157 C 1824.339 161.478, 1824.013 466.620, 1824.008 1137.995 L 1824 2112.490 1688.750 1977.368 C 1597.880 1886.584, 1551.440 1840.875, 1547.221 1838.066 C 1540.033 1833.281, 1530.405 1828.985, 1521 1826.366 C 1511.910 1823.834, 1485.363 1823.833, 1476 1826.363 C 1460.942 1830.432, 1443.757 1840.936, 1433.056 1852.612 C 1415.022 1872.288, 1407.106 1896.615, 1410.040 1923.339 C 1411.806 1939.426, 1416.358 1951.490, 1425.759 1965 C 1430.688 1972.084, 1842.803 2385.354, 1856.013 2396.461 C 1868.334 2406.819, 1882.291 2413.425, 1897.429 2416.061 C 1907.376 2417.793, 1928.581 2416.749, 1937.829 2414.071 C 1946.992 2411.418, 1957.476 2406.279, 1965.403 2400.554 C 1974.201 2394.200, 2397.797 1969.885, 2402.215 1963 C 2407.776 1954.335, 2411.792 1945.748, 2414.233 1937.299 C 2416.182 1930.552, 2416.477 1927.274, 2416.418 1913 C 2416.356 1897.998, 2416.102 1895.683, 2413.617 1887.500 C 2409.098 1872.616, 2401.994 1861.141, 2389.895 1849.177 C 2374.122 1833.580, 2355.761 1825.495, 2333.422 1824.308 C 2311.967 1823.169, 2295.700 1827.420, 2278 1838.792 C 2274.533 1841.019, 2222.699 1892.061, 2137.756 1976.895 L 2003.011 2111.464 2002.750 1134.982 L 2002.488 158.500 2000.265 150.274 C 1993.390 124.839, 1977.498 105.102, 1953.743 92.499 C 1941.286 85.890, 1933.089 83.903, 1916.500 83.469 C 1908.800 83.268, 1900.202 83.517, 1897.393 84.023 M 540.512 546.065 C 514.327 550.461, 488.631 570.269, 476.507 595.404 C 469.644 609.632, 468.565 614.863, 468.544 634 C 468.528 648.244, 468.838 651.586, 470.813 658.443 C 477.637 682.144, 495.441 703.428, 517.586 714.359 C 528.508 719.751, 537.181 721.982, 551.487 723.081 C 559.966 723.733, 712.916 723.970, 990.500 723.763 C 1382.352 723.471, 1417.033 723.313, 1423.152 721.796 C 1455.878 713.678, 1481.111 689.796, 1490.240 658.298 C 1492.180 651.603, 1492.500 648.166, 1492.500 634 C 1492.500 619.723, 1492.188 616.422, 1490.185 609.500 C 1481.971 581.111, 1457.733 556.674, 1429 547.813 L 1421.500 545.500 983.500 545.364 C 742.600 545.289, 543.255 545.604, 540.512 546.065 M 152.500 1008.651 C 118.500 1015.900, 89.570 1046.099, 84.054 1080.099 C 82.722 1088.306, 82.722 1103.694, 84.054 1111.901 C 89.237 1143.845, 115.485 1172.922, 147.500 1182.185 L 155.500 1184.500 788 1184.500 L 1420.500 1184.500 1428.285 1182.243 C 1457.391 1173.807, 1481.809 1149.451, 1490.185 1120.500 C 1492.188 1113.578, 1492.500 1110.277, 1492.500 1096 C 1492.500 1081.758, 1492.186 1078.414, 1490.203 1071.568 C 1481.858 1042.750, 1458.146 1018.880, 1428.815 1009.772 L 1421.500 1007.500 790.500 1007.329 C 234.359 1007.179, 158.670 1007.335, 152.500 1008.651"
					stroke="none"
					fill="currentColor"
					fillRule="evenodd"
				/>
			</svg>
			<Listbox onChange={innerOnChange} value={innerValue} multiple={false}>
				{({ open }) => (
					<InputAttachedFlyout
						scale="small"
						matchWidth={true}
						open={open}
						flyout={({ flyoutRef, flyoutProps, flyoutOptionProps, flyoutOptionCheckmark }) => (
							<ListboxOptions {...flyoutProps} ref={flyoutRef}>
								{items.map((item, i) => (
									<ListboxOption key={`option-${i}`} value={item.value} {...flyoutOptionProps}>
										{flyoutOptionCheckmark}
										{item.label}
									</ListboxOption>
								))}
							</ListboxOptions>
						)}
					>
						{({ referenceRef }) => (
							<FilterAssembly label={label} active={true} showLabel={showLabel}>
								{({ id }) => (
									<ListboxButton id={id} className="w-full">
										<InputAssembly ref={referenceRef} scale="small">
											{({ inputProps }) => (
												<>
													<div {...inputProps}>
														{items.find(i => i.value === innerValue)?.label ?? (
															<span>&nbsp;</span>
														)}
													</div>
													<div>
														<ChevronDownIcon className="w-4 data-[open]:rotate-180 transition-all" />
													</div>
												</>
											)}
										</InputAssembly>
									</ListboxButton>
								)}
							</FilterAssembly>
						)}
					</InputAttachedFlyout>
				)}
			</Listbox>
		</div>
	)
}

const SorterConfigContext = createContext({
	showIcon: false,
	showLabel: true,
})

// TODO: support scale
export type SorterConfigProps = {
	showIcon?: boolean
	showLabel?: boolean
	children: ReactNode
}

export function SorterConfig({ showIcon = false, showLabel = true, children }: SorterConfigProps) {
	return (
		<SorterConfigContext.Provider value={{ showIcon, showLabel }}>
			{children}
		</SorterConfigContext.Provider>
	)
}
